export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-4">Privacy Policy for Junk.fun</h1>
        <p className="text-gray-400 mb-8">Last Updated: January 26, 2026</p>

        <div className="space-y-8">
          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Introduction</h2>
            <p className="text-gray-300 leading-relaxed">
              Junk.fun ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our platform and services.
            </p>
          </section>

          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Information We Collect</h2>

            <h3 className="text-xl font-semibold mb-3 mt-6">Blockchain Information</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong>Wallet Addresses:</strong> We collect your public Solana wallet address when you connect to our app</li>
              <li><strong>Transaction Data:</strong> On-chain transaction records related to token/NFT burns and prize distributions</li>
              <li><strong>Token Holdings:</strong> Information about your token and NFT holdings for burn eligibility</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">Usage Information</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong>App Interactions:</strong> Actions you take within the app (burns, chest openings, leaderboard views)</li>
              <li><strong>Device Information:</strong> Device type, operating system version, and app version</li>
              <li><strong>Performance Data:</strong> Error logs and crash reports for app improvement</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">Information We Do NOT Collect</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>We do NOT collect personally identifiable information (name, email, phone)</li>
              <li>We do NOT collect your private keys or seed phrases</li>
              <li>We do NOT track your location</li>
              <li>We do NOT sell your data to third parties</li>
            </ul>
          </section>

          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">How We Use Your Information</h2>
            <p className="text-gray-300 mb-4">We use the collected information to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>Provide and maintain the Junk.fun service</li>
              <li>Process your burns and prize distributions on-chain</li>
              <li>Display leaderboards and seasonal rankings</li>
              <li>Improve app performance and user experience</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Data Storage and Security</h2>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong>Blockchain Data:</strong> All transaction data is stored on the Solana blockchain and is publicly accessible</li>
              <li><strong>Off-Chain Data:</strong> User statistics and leaderboard data are stored securely on our servers</li>
              <li><strong>Security Measures:</strong> We implement industry-standard security measures to protect your data</li>
              <li><strong>No Custodial Access:</strong> We never have access to your private keys or funds</li>
            </ul>
          </section>

          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Third-Party Services</h2>
            <p className="text-gray-300 mb-4">We use the following third-party services:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li><strong>Solana RPC Providers:</strong> For blockchain interactions (Helius)</li>
              <li><strong>Mobile Wallet Adapters:</strong> For secure wallet connections (Phantom, Solflare, Backpack)</li>
              <li><strong>Analytics Services:</strong> For app performance monitoring</li>
            </ul>
            <p className="text-gray-300 mt-4">These services may have their own privacy policies.</p>
          </section>

          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Your Rights</h2>
            <p className="text-gray-300 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>Access the data we store about your wallet address</li>
              <li>Request deletion of off-chain data (on-chain data cannot be deleted)</li>
              <li>Disconnect your wallet at any time</li>
              <li>Opt out of analytics tracking</li>
            </ul>
          </section>

          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Children's Privacy</h2>
            <p className="text-gray-300">
              Junk.fun is not intended for users under 18 years of age. We do not knowingly collect information from children under 18.
            </p>
          </section>

          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Changes to This Policy</h2>
            <p className="text-gray-300">
              We may update this Privacy Policy from time to time. Changes will be posted on our platform with an updated "Last Updated" date.
            </p>
          </section>

          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
            <p className="text-gray-300 mb-4">If you have questions about this Privacy Policy, contact us at:</p>
            <ul className="list-none text-gray-300 space-y-2">
              <li>Website: <a href="https://junk.fun" className="text-cyan-400 hover:underline">https://junk.fun</a></li>
              <li>Email: privacy@junk.fun</li>
            </ul>
          </section>

          <section className="bg-gray-800/50 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-4">Compliance</h2>
            <p className="text-gray-300 mb-4">This Privacy Policy complies with:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2">
              <li>General Data Protection Regulation (GDPR)</li>
              <li>California Consumer Privacy Act (CCPA)</li>
              <li>Other applicable data protection laws and regulations</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
